import React, { useMemo, useState, useEffect } from 'react';
import { fetchFullRates } from '../../services/mapService';

export default function BillingSummaryPanel({ segments, devices, onClose, darkMode }) {
  const bg = darkMode ? '#1a1a2e' : '#ffffff';
  const text = darkMode ? '#e0e0e0' : '#333';
  const border = darkMode ? '#333' : '#ddd';
  const green = '#4CAF50';

  // Client billing rates — fetched from server (key-gated), never in the bundle
  const [UNIT_RATES, setUnitRates] = useState({});
  useEffect(() => {
    let live = true;
    fetchFullRates('vexus-la-tx-2026')
      .then(d => {
        if (!live || !d?.rates) return;
        const pf = d.priceField || 'vexus';
        const out = {};
        for (const [code, r] of Object.entries(d.rates)) out[code] = r[pf] || 0;
        setUnitRates(out);
      })
      .catch(err => console.error('Failed to load rates:', err));
    return () => { live = false; };
  }, []);

  const summary = useMemo(() => {
    const items = [];
    let totalFootage = 0;
    let totalCost = 0;

    // Conduit segments — group by spec
    const conduitBySpec = {};
    const fiberBySpec = {};

    (segments || []).forEach(seg => {
      const ft = seg.footage || seg.length || 0;
      totalFootage += ft;

      if (seg.lineType === 'fiber') {
        const spec = seg.fiberSpec || seg.conduitSpec || 'fiber';
        fiberBySpec[spec] = (fiberBySpec[spec] || 0) + ft;
      } else {
        const spec = seg.conduitSpec || '2x1.25';
        conduitBySpec[spec] = (conduitBySpec[spec] || 0) + ft;
      }
    });

    // Boring costs
    Object.entries(conduitBySpec).forEach(([spec, footage]) => {
      const ductMatch = spec.match(/^(\d+)x/);
      const ductCount = ductMatch ? parseInt(ductMatch[1]) : 1;
      const code = ductCount <= 3 ? `UG${ductCount}` : 'UG16';
      const rate = UNIT_RATES[code] || 0;
      const cost = footage * rate;
      totalCost += cost;
      items.push({ desc: `Boring ${spec}`, code, qty: footage, unit: 'LF', rate, cost });
    });

    // Pulling costs
    Object.entries({ ...conduitBySpec, ...fiberBySpec }).forEach(([spec, footage]) => {
      const rate = UNIT_RATES.UG4 || 0;
      const cost = footage * rate;
      totalCost += cost;
      items.push({ desc: `Pulling ${spec}`, code: 'UG4', qty: footage, unit: 'LF', rate, cost });
    });

    // Device costs
    const deviceCounts = {};
    (devices || []).forEach(d => {
      const t = d.type || 'unknown';
      deviceCounts[t] = (deviceCounts[t] || 0) + 1;
    });

    const deviceCodeMap = {
      handhole: 'UG17', flowerpot: 'UG12', splitter_1x4: 'UG18',
      splitter_2x8: 'UG18', splice_point: 'FS2', ground_rod: 'UG13',
      slack_loop: 'UG20',
    };

    Object.entries(deviceCounts).forEach(([type, count]) => {
      const code = deviceCodeMap[type] || 'UG20';
      const rate = UNIT_RATES[code] || 0;
      const cost = count * rate;
      totalCost += cost;
      items.push({ desc: type.replace(/_/g, ' '), code, qty: count, unit: 'EA', rate, cost });
    });

    return { items, totalFootage, totalCost };
  }, [segments, devices, UNIT_RATES]);

  return (
    <div style={{
      position: 'absolute', bottom: 50, left: 10, width: 380, maxHeight: 350,
      background: bg, border: `1px solid ${border}`, borderRadius: 8,
      boxShadow: '0 4px 20px rgba(0,0,0,0.3)', zIndex: 1000,
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderBottom: `1px solid ${border}` }}>
        <span style={{ fontWeight: 'bold', color: text, fontSize: 14 }}>Billing Summary</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: text, cursor: 'pointer', fontSize: 18 }}>×</button>
      </div>
      <div style={{ overflowY: 'auto', flex: 1, padding: '8px 14px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${border}` }}>
              <th style={{ textAlign: 'left', padding: '4px 0', color: text, fontWeight: 600 }}>Item</th>
              <th style={{ textAlign: 'right', padding: '4px 4px', color: text, fontWeight: 600 }}>Code</th>
              <th style={{ textAlign: 'right', padding: '4px 4px', color: text, fontWeight: 600 }}>Qty</th>
              <th style={{ textAlign: 'right', padding: '4px 0', color: text, fontWeight: 600 }}>Cost</th>
            </tr>
          </thead>
          <tbody>
            {summary.items.map((item, i) => (
              <tr key={i} style={{ borderBottom: `1px solid ${darkMode ? '#222' : '#f0f0f0'}` }}>
                <td style={{ padding: '4px 0', color: text, textTransform: 'capitalize' }}>{item.desc}</td>
                <td style={{ textAlign: 'right', padding: '4px 4px', color: text, opacity: 0.7, fontFamily: 'monospace' }}>{item.code}</td>
                <td style={{ textAlign: 'right', padding: '4px 4px', color: text }}>{item.qty.toLocaleString()} {item.unit}</td>
                <td style={{ textAlign: 'right', padding: '4px 0', color: green, fontWeight: 600 }}>${item.cost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ padding: '10px 14px', borderTop: `1px solid ${border}`, display: 'flex', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 12, color: text }}>Total: {summary.totalFootage.toLocaleString()} LF</span>
        <span style={{ fontSize: 14, fontWeight: 700, color: green }}>${summary.totalCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
      </div>
    </div>
  );
}
